import { useCallback, useEffect, useMemo } from "react";
import { useLocation, useNavigate, useParams } from "react-router";
import { useTrackedLocalStorage } from "@/hooks/use-tracked-browser-storage";

// Constants
const MAX_RECENT_WEBPAGES = 10;

// Minimal type definition for a recent webpage with only essential fields for storage
interface RecentWebpage {
  url: string;
  page_type: string;
  timestamp: number;
}

/**
 * Hook for managing webpage selection with automatic organization scoping.
 *
 * Features:
 * - Selects webpages and persists them in URL parameters and localStorage
 * - Tracks recently selected webpages with automatic organization isolation
 * - Synchronizes webpage selection between URL and recent webpages
 * - Falls back to previously selected webpage when URL has no webpageUrl
 */
export function useWebpageSelection() {
  const params = useParams<{ webpageUrl?: string }>();
  const navigate = useNavigate();
  const location = useLocation();

  // Manage selected webpage and recent webpages with automatic organization scoping
  const [selectedWebpageUrl, setSelectedWebpageUrl] =
    useTrackedLocalStorage<string>(
      "webpages::explorer::selected-webpage::active",
      "",
    );
  const [recentWebpages, setRecentWebpages] = useTrackedLocalStorage<
    RecentWebpage[]
  >("webpages::explorer::selected-webpage::recent", [], {
    migration: {
      from: {
        key: "recentWebpages",
        storage: "localStorage",
        organizationScoped: true,
      },
      cleanup: true,
    },
  });

  // Get webpage URL from URL path param only
  const webpageUrlFromPath = params.webpageUrl
    ? decodeURIComponent(params.webpageUrl)
    : undefined;
  const storedWebpageUrl = selectedWebpageUrl || undefined;

  const currentSelectedWebpageUrl = webpageUrlFromPath || storedWebpageUrl;

  // Sort recent webpages by timestamp (most recent first)
  const sortedRecentWebpages = useMemo(() => {
    return [...recentWebpages].sort((a, b) => b.timestamp - a.timestamp);
  }, [recentWebpages]);

  // Find the selected webpage in recent webpages list
  const initialWebpage = useMemo(() => {
    if (!currentSelectedWebpageUrl || recentWebpages.length === 0)
      return undefined;
    return recentWebpages.find(
      (webpage) => webpage.url === currentSelectedWebpageUrl,
    );
  }, [currentSelectedWebpageUrl, recentWebpages]);

  // Add a webpage to recent webpages
  const addRecentWebpage = useCallback(
    (webpage: { url: string; page_type: string }) => {
      if (!webpage) return;

      setRecentWebpages((prev) => {
        // Filter out the current webpage if it exists
        const filtered = (prev || []).filter(
          (w: RecentWebpage) => w.url !== webpage.url,
        );

        // Add the webpage to the beginning with timestamp - only essential fields
        return [
          {
            url: webpage.url,
            page_type: webpage.page_type,
            timestamp: Date.now(),
          },
          ...filtered,
        ].slice(0, MAX_RECENT_WEBPAGES);
      });
    },
    [setRecentWebpages],
  );

  // Handle webpage selection
  const handleWebpageChange = useCallback(
    (
      value: string,
      webpage: {
        url: string;
        page_type: string;
      } | null,
    ) => {
      if (value) {
        // URL encode the webpage URL for safe use in the path
        const encodedUrl = encodeURIComponent(value);
        const target = {
          pathname: `/webpages/${encodedUrl}/explorer`,
          search: location.search,
        };
        const isSame =
          location.pathname === target.pathname &&
          location.search === (target.search || "");
        if (!isSame) {
          navigate(target, { replace: true });
        }

        // Also update localStorage for persistence across page loads
        setSelectedWebpageUrl(value);

        // Add to recent webpages if webpage object is provided
        if (webpage) {
          addRecentWebpage(webpage);
        }
      } else {
        // Remove from localStorage only
        setSelectedWebpageUrl("");
      }
    },
    [
      navigate,
      addRecentWebpage,
      setSelectedWebpageUrl,
      location.pathname,
      location.search,
    ],
  );

  // Initialize URL from localStorage if needed
  useEffect(() => {
    // If we have a stored selection but no URL param, update the URL
    if (!webpageUrlFromPath && storedWebpageUrl && selectedWebpageUrl) {
      // For explorer pages, use the new URL pattern
      if (location.pathname === "/webpages/explorer") {
        const encodedUrl = encodeURIComponent(storedWebpageUrl);
        navigate(`/webpages/${encodedUrl}/explorer`, { replace: true });
      }
    }
  }, [
    webpageUrlFromPath,
    storedWebpageUrl,
    selectedWebpageUrl,
    navigate,
    location.pathname,
  ]);

  return {
    selectedWebpageUrl: currentSelectedWebpageUrl,
    initialWebpage,
    handleWebpageChange,
    recentWebpages: sortedRecentWebpages,
  };
}
